import React, { useMemo, useState, useEffect, useCallback } from 'react';
import useAppStore, { useCalculationsSelector } from '../store/appStore';
import useAdminStore from '../store/adminStore';
import UI from '../components/UI';
import PasswordPrompt from '../components/PasswordPrompt';
import { currencyLabel } from '../utils/currencyFormatter';
import type { PrivateCategory, PrivateItem } from '../store/types';
import { calculateGmbhGfConvergence } from '../utils/gmbhConvergence';

// +++ HELPER: INPUT MIT TOOLTIP (WRAPPER) +++
const InputWithTooltip = ({ tooltip, className, label, ...props }: any) => {
    return (
        <div 
            className={`w-full ${className || ''}`} 
            data-tooltip-id="app-tooltip" 
            data-tooltip-content={tooltip}
        >
            {label && <label className="text-[10px] text-pe-muted dark:text-slate-400 font-bold uppercase tracking-wider mb-1.5 block pl-1">{label}</label>}
            <UI.Input 
                {...props} 
                className="w-full bg-pe-input dark:bg-[#08090b] border border-pe-border dark:border-slate-800 text-[#F8FAFC] dark:text-[#F8FAFC] rounded-xl p-3 text-sm focus:border-[#E6C299]/50 outline-none font-mono transition-colors shadow-inner"
                onFocus={(e: any) => e.target.select()} 
            />
        </div>
    );
};

// +++ SMART INTEGER INPUT (LOKAL KOPIERT & ERWEITERT) +++
const SmartIntegerInput = ({ value, onValueChange, suffix, className, placeholder, center, readOnly, type, tooltip, label, ...props }: any) => {
    const toDisplay = useCallback((val: number | string) => {
        if (val === undefined || val === null || val === '') return '';
        if (val === 0 && !readOnly) return '0'; 
        const num = typeof val === 'string' ? parseInt(val, 10) : val;
        if (isNaN(num)) return '';
        return new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(num);
    }, [readOnly]);

    const [display, setDisplay] = useState(toDisplay(value));
    const [active, setActive] = useState(false);

    useEffect(() => {
        if (!active) setDisplay(toDisplay(value));
    }, [value, active, toDisplay]);

    const handleChange = (e: any) => {
        setDisplay(e.target.value);
    };

    const handleFocus = (e: any) => {
        if (readOnly) return;
        setActive(true);
        setDisplay(value ? value.toString() : '');
        const target = e.target;
        setTimeout(() => {
            if (target) target.select();
        }, 0);
    };

    const handleBlur = () => {
        setActive(false);
        if (readOnly) return;
        const raw = display.toString().replace(/[^0-9-]/g, ''); 
        let num = parseInt(raw, 10);
        if (isNaN(num)) num = 0;
        onValueChange(num); 
        setDisplay(toDisplay(num)); 
    };

    return (
        <div 
            className={`w-full ${className || ''}`} 
            data-tooltip-id="app-tooltip" 
            data-tooltip-content={tooltip}
        >
            {label && <label className="text-[10px] text-pe-muted dark:text-slate-400 font-bold uppercase tracking-wider mb-1.5 block pl-1">{label}</label>}
            <UI.Input 
                value={display} 
                onChange={handleChange} 
                onFocus={handleFocus}
                onBlur={handleBlur}
                suffix={suffix} 
                placeholder={placeholder}
                center={center}
                readOnly={readOnly}
                className="w-full bg-pe-input dark:bg-[#08090b] border border-pe-border dark:border-slate-800 text-[#F8FAFC] dark:text-[#F8FAFC] rounded-xl p-3 text-sm focus:border-[#E6C299]/50 outline-none font-mono transition-colors shadow-inner"
                {...props} 
            />
        </div>
    );
};

const UnternehmerwertePage: React.FC = () => {
    const { clientData, setClientData, settings, showFormulas } = useAppStore(state => ({
        clientData: state.clientData,
        setClientData: state.setClientData,
        settings: state.settings,
        showFormulas: state.showFormulas,
    }));

    // 💉 CHIRURGIE: God-Mode Abfrage aus dem Admin-Store
    const isGodMode = useAdminStore(state => !!state.impersonatedClientId);

    const { 
        realUnternehmerSchnittHJ1, 
        realUnternehmerSchnittHJ2,
        bruttoumsatzProStunde,
        erwarteterBruttoUmsatz,
        erwarteterNettoUmsatz,
        erwarteteEinkommenssteuer,
        wochensatz,
        lohnfaktorAktiv,
        lohnfaktorNetto,
        sollumsatz,
        produktiveLohnkostenMitarbeiterOhneAzubis,
        produktiveKostenTabelle,
        prodUnternehmerlohn,
        lohnfortzahlungPercent,
        mwstSatz,
        lohnfaktorData,
        avgSozialabgabenPercent,
    } = useCalculationsSelector();

    const [showGmbhFields, setShowGmbhFields] = useState(false);
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

    const [activeTabId, setActiveTabId] = useState<string | null>(null);

    const isGmbh = clientData.unternehmensform === 'GmbH';
    const currencySymbol = currencyLabel(
        settings.country?.currency,
        (clientData as any)?.land || (settings as any)?.selectedCountryId
    );

    const pgData = clientData.personengesellschaft || { aktiv: false, partner: [] };
    const isMultiFounder = pgData.aktiv;

    useEffect(() => {
        if (isMultiFounder && !activeTabId && pgData.partner.length > 0) {
            setActiveTabId(pgData.partner[0].id);
        }
    }, [isMultiFounder, pgData.partner, activeTabId]);

    const activePartner = isMultiFounder ? pgData.partner.find((p: any) => p.id === activeTabId) : null;

    const unternehmerData = useMemo(() => clientData.unternehmerData || {
        entnahme: 0, sozialkosten: 0, prodWochenstunden: 0, durchschnittsumsatz: 0,
        monatsUmsaetze: Array(12).fill(0), einkommenssteuer: 0, geoeffneteStunden: Array(6).fill(0), prodWochentage: 0
    }, [clientData.unternehmerData]);

    const currentViewData = (isMultiFounder && activePartner) ? activePartner : unternehmerData;

    const kompassBedarf = useMemo(() => {
        const cats = clientData.privateCostData?.monthlyCats || [];
        let living = 0;
        let social = 0;
        cats.forEach((cat: PrivateCategory) => {
            const catSum = (cat.items || []).reduce((s: number, i: PrivateItem) => s + (Number(i.value) || 0), 0);
            if (cat.type === 'social') social += catSum;
            else living += catSum;
        });
        const periodicShare = (clientData.privateCostData?.periodicItems || []).reduce(
            (acc: number, item: PrivateItem) => acc + ((Number(item.value) || 0) / (item.frequency || 12)),
            0
        );
        const neededLiving = living + periodicShare;
        const neededSocial = social;
        const neededTotal = neededLiving + neededSocial;
        // GmbH: Planung = GF-Zielgehalt (gleiche Quelle wie GF-Rechner); sonst Entnahme + Sozialkosten
        const plannedLiving = Number(currentViewData.entnahme) || 0;
        const plannedSocial = Number(currentViewData.sozialkosten) || 0;
        const plannedEntnahmeTotal = plannedLiving + plannedSocial;
        const gfRaw = Number((unternehmerData as any)?.gfZielgehalt);
        const plannedTotal =
            isGmbh
                ? Number.isFinite(gfRaw) && gfRaw > 0
                    ? gfRaw
                    : 5000
                : plannedEntnahmeTotal;
        const diff = plannedTotal - neededTotal;
        return {
            neededLiving,
            neededSocial,
            neededTotal,
            plannedTotal,
            diff,
            isCovered: diff >= 0,
            hasData: neededTotal > 0 || cats.some((c) => (c.items || []).some((i) => (Number(i.value) || 0) > 0)),
        };
    }, [
        clientData.privateCostData,
        currentViewData.entnahme,
        currentViewData.sozialkosten,
        unternehmerData,
        isGmbh,
    ]);

    const handleDataChange = (key: string, value: any) => {
        if (isMultiFounder && activePartner) {
            setClientData(prev => ({
                ...prev,
                personengesellschaft: {
                    ...prev.personengesellschaft!,
                    partner: prev.personengesellschaft!.partner.map((p: any) => p.id === activePartner.id ? { ...p, [key]: Number(value) } : p)
                }
            }));
        } else {
            setClientData(prev => ({ ...prev, unternehmerData: { ...(prev.unternehmerData || {}), [key]: Number(value) } }));
        }
    };

    const handleNameChange = (name: string) => {
        if (isMultiFounder && activePartner) {
            setClientData(prev => ({
                ...prev,
                personengesellschaft: {
                    ...prev.personengesellschaft!,
                    partner: prev.personengesellschaft!.partner.map((p: any) => p.id === activePartner.id ? { ...p, name } : p)
                }
            }));
        }
    };

    const handleMonatsumsatzChange = (index: number, value: number) => {
        if (isMultiFounder && activePartner) {
            const newUmsaetze = [...(activePartner.monatsUmsaetze || Array(12).fill(0))];
            newUmsaetze[index] = Number(value);
            const newData = { ...activePartner, monatsUmsaetze: newUmsaetze };
            if (newUmsaetze.some((u: number) => u > 0)) {
                newData.durchschnittsumsatz = 0;
            }
            setClientData(prev => ({
                ...prev,
                personengesellschaft: {
                    ...prev.personengesellschaft!,
                    partner: prev.personengesellschaft!.partner.map((p: any) => p.id === activePartner.id ? newData : p)
                }
            }));
        } else {
            const newUmsaetze = [...(unternehmerData.monatsUmsaetze || Array(12).fill(0))]; 
            newUmsaetze[index] = Number(value); 
            setClientData(prev => { 
                const newUnternehmerData = { ...(prev.unternehmerData || {}), monatsUmsaetze: newUmsaetze }; 
                if (newUmsaetze.some(u => u > 0)) { newUnternehmerData.durchschnittsumsatz = 0; } 
                return { ...prev, unternehmerData: newUnternehmerData }; 
            }); 
        }
    };

    const handleStundenChange = (index: number, value: number) => { const sanitizedValue = Math.max(0, Math.min(24, Number(value) || 0)); const newStunden = [...(unternehmerData.geoeffneteStunden || Array(6).fill(0))]; newStunden[index] = sanitizedValue; setClientData(prev => ({ ...prev, unternehmerData: { ...prev.unternehmerData, geoeffneteStunden: newStunden } })); };

    const togglePersonengesellschaft = () => {
        setClientData(prev => {
            const isNowActive = !prev.personengesellschaft?.aktiv;
            // Immer neue Array-Referenz — niemals das Store-Array in-place mutieren
            const newPartners = [...(prev.personengesellschaft?.partner || [])];
            
            if (isNowActive && newPartners.length === 0) {
                const newId = Date.now().toString(36) + Math.random().toString(36).substring(2);
                const src = prev.unternehmerData || {};
                // Deep Clone: keine geteilten Array-/Objekt-Referenzen mit unternehmerData
                const srcMonats = Array.isArray(src.monatsUmsaetze) ? src.monatsUmsaetze : Array(12).fill(0);
                newPartners.push({
                    id: newId,
                    name: 'Partner 1',
                    entnahme: src.entnahme || 0,
                    sozialkosten: src.sozialkosten || 0,
                    einkommenssteuer: src.einkommenssteuer || 0,
                    prodWochenstunden: src.prodWochenstunden || 0,
                    prodWochentage: src.prodWochentage || 0,
                    monatsUmsaetze: [...srcMonats],
                    durchschnittsumsatz: src.durchschnittsumsatz || 0
                });
                setActiveTabId(newId);
            }

            // Toggle Off: nur aktiv=false — kein Rückschreiben GbR → Einzelunternehmer
            return {
                ...prev,
                personengesellschaft: {
                    ...prev.personengesellschaft,
                    aktiv: isNowActive,
                    partner: newPartners
                }
            };
        });
    };

    const addPartner = () => {
        setClientData(prev => {
            const pg = prev.personengesellschaft || { aktiv: true, partner: [] };
            if (pg.partner.length >= 5) return prev;
            
            const newId = Date.now().toString(36) + Math.random().toString(36).substring(2);
            setActiveTabId(newId); 
            
            return {
                ...prev,
                personengesellschaft: {
                    ...pg,
                    partner: [...pg.partner, { 
                        id: newId, 
                        name: `Partner ${pg.partner.length + 1}`, 
                        entnahme: 0, sozialkosten: 0, einkommenssteuer: 0,
                        prodWochenstunden: 0, prodWochentage: 0, 
                        monatsUmsaetze: Array(12).fill(0), durchschnittsumsatz: 0 
                    }]
                }
            };
        });
    };

    const removePartner = (id: string) => {
        setClientData(prev => {
            const pg = prev.personengesellschaft;
            if (!pg) return prev;
            const newPartners = pg.partner.filter((p: any) => p.id !== id);
            
            if (activeTabId === id && newPartners.length > 0) {
                setActiveTabId(newPartners[0].id);
            }
            
            return {
                ...prev,
                personengesellschaft: {
                    ...pg,
                    partner: newPartners
                }
            };
        });
    };

    let aggErwarteteSteuer = erwarteteEinkommenssteuer;
    let aggErwarteterBrutto = erwarteterBruttoUmsatz;
    let aggErwarteterNetto = erwarteterNettoUmsatz;
    let aggBruttoumsatzProStunde = bruttoumsatzProStunde;

    if (isMultiFounder) {
        let totalSteuer = 0;
        let totalBrutto = 0;
        let totalStunden = 0;

        pgData.partner.forEach((p: any) => {
            totalSteuer += ((p.entnahme || 0) + (p.sozialkosten || 0)) * ((p.einkommenssteuer || 0) / 100);
            
            const activeMonths = (p.monatsUmsaetze || []).filter((m: number) => m > 0);
            const avgUmsatz = (p.durchschnittsumsatz || 0) > 0 ? p.durchschnittsumsatz : (activeMonths.length > 0 ? activeMonths.reduce((a: number, b: number) => a + b, 0) / activeMonths.length : 0);
            totalBrutto += avgUmsatz;

            totalStunden += (p.prodWochenstunden || 0);
        });

        const effectiveMwst =
            mwstSatz !== undefined && mwstSatz !== null
                ? Number(mwstSatz)
                : clientData.isVatExempt
                    ? 0
                    : (settings.country?.MwSt_Satz ?? 19);
        const mwstFaktor = 1 + (effectiveMwst / 100);

        aggErwarteteSteuer = totalSteuer;
        aggErwarteterBrutto = totalBrutto;
        aggErwarteterNetto = totalBrutto / mwstFaktor;
        aggBruttoumsatzProStunde = (totalStunden > 0) ? (totalBrutto / (totalStunden * (wochensatz || 4.33))) : 0;
    }

    // 💉 NOBLE SLATE FORMULA RESULT BOX
    const FormulaResult = ({ label, value, formula, show, tooltip }: { label: string; value: string; formula: string; show: boolean; tooltip?: string }) => {
        return (
            <div className="bg-pe-tile-header dark:bg-[#08090b] border border-pe-border dark:border-slate-800 rounded-xl p-4 flex flex-col gap-2 relative group hover:border-[#E6C299]/30 transition-colors" data-tooltip-id="app-tooltip" data-tooltip-content={tooltip}>
                <span className="text-[10px] text-stone-500 dark:text-slate-500 font-bold uppercase tracking-widest group-hover:text-pe-accent-text dark:group-hover:text-[#E6C299] transition-colors">{label}</span>
                <div className="text-pe-accent-text dark:text-[#E6C299] font-mono text-xl font-black">{value}</div>
                {show && (
                    <div className="text-[9px] text-emerald-700 dark:text-emerald-500 font-mono bg-emerald-100/60 dark:bg-emerald-950/20 px-2 py-1.5 rounded border border-emerald-300 dark:border-emerald-900/50 mt-1 truncate" title={formula}>
                        Formel: {formula}
                    </div>
                )}
            </div>
        );
    };

    const formatCurrency = (val: number) => new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val);

    useEffect(() => {
        if (!isGmbh && showGmbhFields) setShowGmbhFields(false);
    }, [isGmbh, showGmbhFields]);

    const handlePasswordConfirm = (password: string) => {
        if (password === '90') {
            setShowGmbhFields(true);
            setIsPasswordModalOpen(false);
        } else {
            alert('Falsches Passwort');
        }
    };

    const wochentage = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];
    const monate = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
    const showMonatsTabelle = (currentViewData.durchschnittsumsatz || 0) === 0;
    const showDurchschnittsFeld = (currentViewData.monatsUmsaetze || []).every((u: number) => u === 0);

    return (
        <div className="animate-fade-in pb-24">
            <PasswordPrompt isOpen={isPasswordModalOpen} onClose={() => setIsPasswordModalOpen(false)} onConfirm={handlePasswordConfirm} />
            
            <div className="max-w-7xl mx-auto space-y-8">
                
                {/* 💉 NOBLE SLATE HEADER */}
                <div className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-end border-b border-pe-border dark:border-slate-800 pb-6 gap-4">
                    <div>
                        <h1 className="text-3xl font-black text-pe-text dark:text-white tracking-widest uppercase mb-2">Unternehmerwerte</h1>
                        <p className="text-pe-muted dark:text-slate-400 text-sm">Finanzbedarf, Arbeitszeiten & monetäre Steuerung</p>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-3">
                        {isGmbh && showGmbhFields && (
                            <button 
                                onClick={() => setShowGmbhFields(false)}
                                className="px-4 py-2 bg-pe-card dark:bg-[#15171e] hover:bg-pe-hover dark:hover:bg-[#0c0d11] text-pe-muted dark:text-slate-300 border border-pe-border-strong dark:border-slate-700 hover:border-stone-400 dark:hover:border-slate-500 rounded-lg text-xs font-bold uppercase tracking-widest transition-colors"
                            >
                                Standard-Ansicht
                            </button>
                        )}
                        {isGodMode && (
                            <button 
                                onClick={togglePersonengesellschaft}
                                className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-widest transition-colors border ${pgData.aktiv ? 'bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/30 hover:bg-orange-500/20' : 'bg-pe-card dark:bg-[#15171e] text-pe-muted dark:text-slate-400 border-pe-border-strong dark:border-slate-700 hover:text-pe-text dark:hover:text-white'}`}
                            >
                                {pgData.aktiv ? "PG Deaktivieren" : "Auf PG umstellen"}
                            </button>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    
                    {/* 💉 NOBLE SLATE: GEÖFFNETE STUNDEN (LINKS) */}
                    <div className="lg:col-span-4 space-y-8">
                        <div className="bg-pe-card dark:bg-[#15171e] p-6 md:p-8 rounded-2xl border border-pe-border dark:border-white/5 shadow-2xl relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-slate-600 to-transparent opacity-50"></div>
                            <h2 className="text-sm font-black text-pe-text dark:text-white uppercase tracking-widest mb-6 flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-stone-500 dark:text-slate-500"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                Geöffnete Stunden
                            </h2>
                            <div className="space-y-4">
                                {wochentage.map((tag, index) => (
                                    <div key={tag} className="grid grid-cols-2 items-center gap-4 bg-pe-tile-header dark:bg-[#08090b] p-3 rounded-xl border border-pe-border dark:border-slate-800">
                                        <span className="text-pe-muted dark:text-slate-400 font-bold text-xs uppercase tracking-wider pl-2">{tag}</span>
                                        <InputWithTooltip 
                                            type="number"
                                            min={0}
                                            max={24}
                                            value={(unternehmerData.geoeffneteStunden || [])[index]} 
                                            onChange={(e: any) => handleStundenChange(index, Number(e.target.value))}
                                            onKeyDown={(e: any) => {
                                                if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === '-') {
                                                    e.preventDefault();
                                                }
                                            }}
                                            placeholder="0" 
                                            suffix="Std." 
                                            tooltip={`Wie lange ist der Salon am ${tag} geöffnet?`}
                                            className="!mb-0" // Verhindert margin-bottom im Input-Wrapper
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* 💉 NOBLE SLATE: WERT-EINGABEN (RECHTS) */}
                    <div className="lg:col-span-8 space-y-8">
                        
                        {isMultiFounder && (
                            <div className="flex flex-wrap gap-2 mb-2">
                                {pgData.partner.map((p: any) => (
                                    <button 
                                        key={p.id}
                                        onClick={() => setActiveTabId(p.id)}
                                        className={`px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-widest transition-all ${
                                            activeTabId === p.id 
                                                ? 'bg-[#E6C299] text-black shadow-[0_0_15px_rgba(230,194,153,0.3)]' 
                                                : 'bg-pe-card dark:bg-[#15171e] text-stone-500 dark:text-slate-500 hover:text-pe-text dark:hover:text-white border border-pe-border dark:border-slate-800 hover:border-stone-400 dark:hover:border-slate-600'
                                        }`}
                                    >
                                        {p.name || 'Partner'}
                                    </button>
                                ))}
                                {pgData.partner.length < 5 && (
                                    <button 
                                        onClick={addPartner}
                                        className="px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-widest text-emerald-700 dark:text-emerald-500 border border-emerald-700/30 dark:border-emerald-500/30 hover:bg-emerald-500/10 transition-colors flex items-center gap-1"
                                    >
                                        + Neu
                                    </button>
                                )}
                            </div>
                        )}

                        {(!isGmbh || showGmbhFields) ? (
                            <>
                                {isMultiFounder && activePartner && (
                                    <div className="bg-pe-card dark:bg-[#15171e] p-6 md:p-8 rounded-2xl border border-orange-500/20 shadow-2xl relative overflow-hidden group">
                                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-orange-500/50 to-transparent"></div>
                                        <button 
                                            onClick={() => removePartner(activePartner.id)}
                                            className="absolute top-6 right-6 text-stone-400 dark:text-slate-600 hover:text-red-600 dark:hover:text-red-500 transition-colors"
                                            title="Diesen Partner löschen"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>
                                        </button>
                                        <h2 className="text-sm font-black text-orange-700 dark:text-orange-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                                            Partner Identität
                                        </h2>
                                        <div className="max-w-md">
                                            <InputWithTooltip 
                                                label="Name des Partners" 
                                                type="text" 
                                                value={activePartner.name} 
                                                onChange={(e: any) => handleNameChange(e.target.value)} 
                                                placeholder="Name eintragen..." 
                                                tooltip="Der Name dieses Gesellschafters."
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="bg-pe-card dark:bg-[#15171e] p-6 md:p-8 rounded-2xl border border-pe-border dark:border-white/5 shadow-2xl relative overflow-hidden">
                                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#E6C299]/30 to-transparent"></div>
                                    <h2 className="text-sm font-black text-pe-text dark:text-white uppercase tracking-widest mb-6 flex items-center gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#E6C299]"><rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/></svg>
                                        Finanzbedarf {isMultiFounder && activePartner ? `(${activePartner.name || 'Partner'})` : ''}
                                    </h2>

                                    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,17.5rem)_minmax(0,1fr)] gap-6 lg:gap-10 items-stretch">
                                        <div className="min-w-0 flex flex-col gap-4">
                                            <SmartIntegerInput
                                                label="Private Entnahme (Lebenshaltung)"
                                                value={currentViewData.entnahme}
                                                onValueChange={(val: number) => handleDataChange('entnahme', val)}
                                                suffix={currencySymbol}
                                                tooltip="Was benötigst du privat monatlich zum Leben? (Miete, Essen, Urlaub, etc.)"
                                            />
                                            <SmartIntegerInput
                                                label="Sozialkosten (Rente + KK)"
                                                value={currentViewData.sozialkosten}
                                                onValueChange={(val: number) => handleDataChange('sozialkosten', val)}
                                                suffix={currencySymbol}
                                                tooltip="Deine monatlichen Kosten für Private Krankenversicherung und Altersvorsorge."
                                            />
                                            <div className="pt-4 mt-auto border-t border-pe-border dark:border-slate-800">
                                                <InputWithTooltip
                                                    label="Gesamtlohn (Kalkuliert)"
                                                    value={formatCurrency((currentViewData.entnahme || 0) + (currentViewData.sozialkosten || 0))}
                                                    readOnly
                                                    suffix={currencySymbol}
                                                    tooltip="Summe aus Entnahme und Sozialkosten. Das ist dein kalkulatorisches 'Brutto'."
                                                />
                                            </div>
                                        </div>

                                        <div className="flex h-full flex-col rounded-2xl border border-[#E6C299]/25 bg-pe-tile-header/80 dark:bg-[#08090b]/80 p-4 md:p-5 shadow-inner">
                                            <div className="mb-4 flex items-center justify-between gap-2">
                                                <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[#E6C299]">
                                                    Privatkosten-Kompass
                                                </p>
                                                <span
                                                    className={`rounded-md px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
                                                        !kompassBedarf.hasData
                                                            ? 'bg-stone-500/20 text-stone-400'
                                                            : kompassBedarf.isCovered
                                                              ? 'bg-emerald-500/15 text-emerald-400'
                                                              : 'bg-rose-500/15 text-rose-400'
                                                    }`}
                                                >
                                                    {!kompassBedarf.hasData
                                                        ? 'Keine Daten'
                                                        : kompassBedarf.isCovered
                                                          ? 'Gedeckt'
                                                          : 'Unterdeckt'}
                                                </span>
                                            </div>

                                            <div className="flex flex-1 flex-col justify-center gap-3.5 text-sm">
                                                <div className="flex items-baseline justify-between gap-3">
                                                    <span className="text-[11px] text-pe-muted dark:text-slate-400">Bedarf Lebenshaltung</span>
                                                    <span className="font-mono text-pe-accent-text dark:text-[#E6C299] tabular-nums">
                                                        {formatCurrency(Math.round(kompassBedarf.neededLiving))}{' '}
                                                        <span className="text-[10px] text-stone-500">{currencySymbol}</span>
                                                    </span>
                                                </div>
                                                <div className="flex items-baseline justify-between gap-3">
                                                    <span className="text-[11px] text-pe-muted dark:text-slate-400">Bedarf Sozialkosten</span>
                                                    <span className="font-mono text-pe-accent-text dark:text-[#E6C299] tabular-nums">
                                                        {formatCurrency(Math.round(kompassBedarf.neededSocial))}{' '}
                                                        <span className="text-[10px] text-stone-500">{currencySymbol}</span>
                                                    </span>
                                                </div>
                                                <div className="flex items-baseline justify-between gap-3 border-t border-pe-border dark:border-slate-800 pt-3">
                                                    <span className="text-[11px] font-bold uppercase tracking-wider text-pe-text dark:text-white">
                                                        Bedarf gesamt
                                                    </span>
                                                    <span className="font-mono text-base font-bold text-pe-accent-text dark:text-[#E6C299] tabular-nums">
                                                        {formatCurrency(Math.round(kompassBedarf.neededTotal))}{' '}
                                                        <span className="text-[10px] font-sans font-bold text-stone-500">{currencySymbol}</span>
                                                    </span>
                                                </div>
                                                <div className="flex items-baseline justify-between gap-3">
                                                    <span className="text-[11px] text-pe-muted dark:text-slate-400">Differenz zur Planung</span>
                                                    <span
                                                        className={`font-mono font-bold tabular-nums ${
                                                            !kompassBedarf.hasData
                                                                ? 'text-stone-500'
                                                                : kompassBedarf.isCovered
                                                                  ? 'text-emerald-500 dark:text-emerald-400'
                                                                  : 'text-rose-500 dark:text-rose-400'
                                                        }`}
                                                    >
                                                        {kompassBedarf.diff > 0 ? '+' : ''}
                                                        {formatCurrency(Math.round(kompassBedarf.diff))}{' '}
                                                        <span className="text-[10px] font-sans font-normal text-stone-500">{currencySymbol}</span>
                                                    </span>
                                                </div>
                                            </div>

                                            <p className="mt-4 text-[10px] leading-relaxed text-stone-500 dark:text-slate-500">
                                                Live aus dem Privatkosten-Kompass — inkl. periodischer Posten anteilig / Monat.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-pe-card dark:bg-[#15171e] p-6 md:p-8 rounded-2xl border border-pe-border dark:border-white/5 shadow-2xl relative overflow-hidden">
                                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#E6C299]/30 to-transparent"></div>
                                    <h2 className="text-sm font-black text-pe-text dark:text-white uppercase tracking-widest mb-6 flex items-center gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#E6C299]"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                        Arbeitszeiten & Steuern
                                    </h2>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        <InputWithTooltip 
                                            label="Persönlicher Steuersatz" 
                                            type="number" 
                                            value={currentViewData.einkommenssteuer} 
                                            onChange={(e: any) => handleDataChange('einkommenssteuer', e.target.value)} 
                                            suffix="%" 
                                            tooltip="Dein persönlicher Einkommenssteuersatz (in der Regel zwischen 30% und 42%)."
                                        />
                                        <InputWithTooltip 
                                            label="Prod. Std / Woche" 
                                            type="number" 
                                            value={currentViewData.prodWochenstunden} 
                                            onChange={(e: any) => handleDataChange('prodWochenstunden', e.target.value)} 
                                            suffix="Std." 
                                            tooltip="Wie viele Stunden arbeitest du effektiv AM Kunden pro Woche?"
                                        />
                                        <InputWithTooltip 
                                            label="Prod. Tage / Woche" 
                                            type="number" 
                                            value={currentViewData.prodWochentage} 
                                            onChange={(e: any) => handleDataChange('prodWochentage', e.target.value)} 
                                            suffix="Tage" 
                                            tooltip="Anzahl der Tage pro Woche, an denen du im Salon arbeitest."
                                        />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="space-y-4">
                            <div className="bg-[#0A0A0A] dark:bg-[#0A0A0A] p-6 md:p-8 rounded-2xl border border-[#1E293B] shadow-2xl relative overflow-hidden">
                                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#DCB68A]/50 to-transparent"></div>
                                <h2 className="text-sm font-black text-[#DCB68A] uppercase tracking-widest mb-2 flex items-center gap-2">
                                    GmbH · GF-Vergütungsrechner
                                </h2>
                                <p className="text-xs text-slate-400 mb-6 leading-relaxed">
                                    In einer GmbH wird der Unternehmer als Mitarbeiter geführt. Hier die Aufteilung
                                    Zielgehalt ↔ Umsatz am Kunden berechnen — Eintragung in die Mitarbeiter-Verwaltung
                                    bleibt manuell beim Coach.
                                </p>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                                    <SmartIntegerInput
                                        label="GF-Zielgehalt"
                                        value={Number(unternehmerData.gfZielgehalt) || 5000}
                                        onValueChange={(val: number) => handleDataChange('gfZielgehalt', val)}
                                        suffix={currencySymbol}
                                        tooltip="Monatliches Zielgehalt des Geschäftsführers (brutto / kalkulatorisch)."
                                    />
                                    <SmartIntegerInput
                                        label="Umsatz am Kunden"
                                        value={Number(unternehmerData.gfUmsatz) || 8000}
                                        onValueChange={(val: number) => handleDataChange('gfUmsatz', val)}
                                        suffix={currencySymbol}
                                        tooltip="Monatlicher Umsatz, den der GF selbst am Kunden erwirtschaftet."
                                    />
                                </div>

                                {(() => {
                                    const gfZiel = Number(unternehmerData.gfZielgehalt) || 5000;
                                    const gfUmsatz = Number(unternehmerData.gfUmsatz) || 8000;
                                    const currentLf =
                                        Number(lohnfaktorAktiv) > 0
                                            ? Number(lohnfaktorAktiv)
                                            : Number(lohnfaktorNetto) > 0
                                              ? Number(lohnfaktorNetto)
                                              : 3.5;
                                    const ohneAzubis =
                                        Number(produktiveLohnkostenMitarbeiterOhneAzubis) ||
                                        Math.max(
                                            0,
                                            (Number(produktiveKostenTabelle) || 0) -
                                                (Number(prodUnternehmerlohn) || 0)
                                        );
                                    const sicherheitRaw = lohnfaktorData?.sicherheitsfaktor;
                                    const sicherheitsfaktor =
                                        sicherheitRaw === null ||
                                        sicherheitRaw === undefined ||
                                        sicherheitRaw === ''
                                            ? 25
                                            : Number(sicherheitRaw) || 0;
                                    // Dynamischer AG-LNK: Team-Ø aus Selektor (fällt auf Country-Lohnnebenkosten zurück)
                                    const lohnnebenkostenProzent = Number(
                                        avgSozialabgabenPercent > 0
                                            ? avgSozialabgabenPercent
                                            : (settings.country as any)?.Lohnnebenkosten ??
                                              (settings.country as any)?.lohnnebenkosten ??
                                              0
                                    );
                                    const conv = calculateGmbhGfConvergence({
                                        gfZielgehalt: gfZiel,
                                        gfUmsatz,
                                        currentLohnfaktor: currentLf,
                                        sollumsatz: Number(sollumsatz) || 0,
                                        produktiveLohnkostenMitarbeiterOhneAzubis: ohneAzubis,
                                        lohnfortzahlungPercent:
                                            Number(lohnfortzahlungPercent) || 20,
                                        mwstSatz: Number(mwstSatz) || 0,
                                        sicherheitsfaktor,
                                        lohnnebenkostenProzent,
                                    });
                                    const fmt = (n: number) =>
                                        new Intl.NumberFormat('de-DE', {
                                            maximumFractionDigits: 0,
                                        }).format(n);
                                    const fmtLf = (n: number) =>
                                        n.toLocaleString('de-DE', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        });
                                    return (
                                        <>
                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                                                <div className="bg-[#1E293B]/60 border border-[#DCB68A]/25 rounded-xl p-4 text-center">
                                                    <div className="text-[10px] uppercase tracking-widest text-[#DCB68A] font-bold mb-2">
                                                        Ziel-Multiplikator
                                                    </div>
                                                    <div className="text-xs text-slate-400 mb-2">inkl. GF (Schatten)</div>
                                                    <div className="text-2xl font-mono font-black text-[#DCB68A]">
                                                        {fmtLf(conv.convergedLohnfaktor)}
                                                    </div>
                                                </div>
                                                <div className="bg-[#1E293B]/60 border border-[#DCB68A]/25 rounded-xl p-4 text-center">
                                                    <div className="text-[10px] uppercase tracking-widest text-[#DCB68A] font-bold mb-2">
                                                        Produktiver Anteil
                                                    </div>
                                                    <div className="text-xs text-slate-400 mb-2">Kundenbedienung</div>
                                                    <div className="text-2xl font-mono font-black text-[#DCB68A]">
                                                        {fmt(conv.produktiverLohn)} {currencySymbol}
                                                    </div>
                                                </div>
                                                <div className="bg-[#1E293B]/60 border border-[#DCB68A]/25 rounded-xl p-4 text-center">
                                                    <div className="text-[10px] uppercase tracking-widest text-[#DCB68A] font-bold mb-2">
                                                        Unproduktiver Anteil
                                                    </div>
                                                    <div className="text-xs text-slate-400 mb-2">Management / Leitung</div>
                                                    <div className="text-2xl font-mono font-black text-[#DCB68A]">
                                                        {fmt(conv.unproduktiverLohn)} {currencySymbol}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="text-[11px] font-mono text-[#DCB68A]/80 bg-[#08090b] border border-[#1E293B] rounded-lg p-3 mb-3">
                                                Umsatz ({fmt(gfUmsatz)} {currencySymbol}) / Ziel-LF (
                                                {fmtLf(conv.convergedLohnfaktor)}) ={' '}
                                                {fmt(conv.produktiverLohn)} {currencySymbol}
                                                <span className="text-slate-500">
                                                    {' '}· AG-LNK {lohnnebenkostenProzent.toLocaleString('de-DE', { maximumFractionDigits: 1 })}%
                                                </span>
                                            </div>
                                            <p className="text-[11px] text-slate-500 leading-relaxed">
                                                Trage diese ermittelten Werte als Mitarbeiter in der Team-Verwaltung ein.
                                                Das Gesamtsystem schwingt sich dann exakt auf den Ziel-Multiplikator ein.
                                                Kein automatischer Write-Back.
                                            </p>
                                        </>
                                    );
                                })()}

                                <div className="mt-6 flex justify-end w-full">
                                    <button 
                                        onClick={() => setIsPasswordModalOpen(true)}
                                        className="px-4 py-2 bg-[#1E293B] hover:bg-slate-700 text-slate-300 border border-slate-600 rounded-lg text-[10px] font-black uppercase tracking-widest transition-colors"
                                    >
                                        Admin: Werte trotzdem bearbeiten
                                    </button>
                                </div>
                            </div>

                            {/* Kompakter Kompass-Streifen unter dem GF-Rechner */}
                            <div className="rounded-xl border border-[#DCB68A]/25 bg-[#0A0A0A] dark:bg-[#08090b]/80 px-4 py-3 shadow-inner">
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                                    <div className="flex items-center gap-2 shrink-0">
                                        <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[#DCB68A]">
                                            Privatkosten-Kompass
                                        </p>
                                        <span
                                            className={`rounded-md px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
                                                !kompassBedarf.hasData
                                                    ? 'bg-stone-500/20 text-stone-400'
                                                    : kompassBedarf.isCovered
                                                      ? 'bg-emerald-500/15 text-emerald-400'
                                                      : 'bg-rose-500/15 text-rose-400'
                                            }`}
                                        >
                                            {!kompassBedarf.hasData
                                                ? 'Keine Daten'
                                                : kompassBedarf.isCovered
                                                  ? 'Gedeckt'
                                                  : 'Unterdeckt'}
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-center gap-x-5 gap-y-2 text-sm min-w-0">
                                        <div className="flex items-baseline justify-between sm:justify-start gap-2">
                                            <span className="text-[10px] text-slate-500 uppercase tracking-wider">Bedarf</span>
                                            <span className="font-mono text-[#DCB68A] tabular-nums text-[13px]">
                                                {formatCurrency(Math.round(kompassBedarf.neededTotal))}{' '}
                                                <span className="text-[9px] text-stone-500">{currencySymbol}</span>
                                            </span>
                                        </div>
                                        <div className="flex items-baseline justify-between sm:justify-start gap-2">
                                            <span className="text-[10px] text-slate-500 uppercase tracking-wider">GF-Ziel</span>
                                            <span className="font-mono text-[#DCB68A] tabular-nums text-[13px]">
                                                {formatCurrency(Math.round(kompassBedarf.plannedTotal))}{' '}
                                                <span className="text-[9px] text-stone-500">{currencySymbol}</span>
                                            </span>
                                        </div>
                                        <div className="flex items-baseline justify-between sm:justify-start gap-2 col-span-2 sm:col-span-1">
                                            <span className="text-[10px] text-slate-500 uppercase tracking-wider">Differenz</span>
                                            <span
                                                className={`font-mono font-bold tabular-nums text-[13px] ${
                                                    !kompassBedarf.hasData
                                                        ? 'text-stone-500'
                                                        : kompassBedarf.isCovered
                                                          ? 'text-emerald-500 dark:text-emerald-400'
                                                          : 'text-rose-500 dark:text-rose-400'
                                                }`}
                                            >
                                                {kompassBedarf.diff > 0 ? '+' : ''}
                                                {formatCurrency(Math.round(kompassBedarf.diff))}{' '}
                                                <span className="text-[9px] font-sans font-normal text-stone-500">{currencySymbol}</span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* 💉 NOBLE SLATE: UMSATZ & RESULTATE (VOLLE BREITE UNTEN) */}
                {(!isGmbh || showGmbhFields) && (
                    <div className="space-y-8 mt-4">
                        <div className="bg-pe-card dark:bg-[#15171e] p-6 md:p-8 rounded-2xl border border-pe-border dark:border-white/5 shadow-2xl relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#E6C299]/30 to-transparent"></div>
                            <h2 className="text-sm font-black text-pe-text dark:text-white uppercase tracking-widest mb-6 flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#E6C299]"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                                Umsatz des produktiven Unternehmers {isMultiFounder && activePartner ? `(${activePartner.name || 'Partner'})` : ''}
                            </h2>
                            
                            {showMonatsTabelle && (
                                <div className="mb-8">
                                    <p className="text-xs text-stone-500 dark:text-slate-500 uppercase tracking-widest mb-4 font-bold">Bruttoumsätze pro Monat</p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-pe-tile-header dark:bg-[#08090b] p-6 rounded-xl border border-pe-border dark:border-slate-800 shadow-inner">
                                        {[0, 1].map((colIndex) => (
                                            <div key={colIndex} className="space-y-3">
                                                {monate.slice(colIndex * 6, colIndex * 6 + 6).map((monat, rowIndex) => { 
                                                    const actualIndex = colIndex * 6 + rowIndex; 
                                                    return (
                                                        <div key={monat} className="flex items-center gap-4">
                                                            <span className="text-pe-muted dark:text-slate-400 text-xs font-bold uppercase tracking-wider w-24">{monat}</span>
                                                            <div className="flex-1">
                                                                <SmartIntegerInput 
                                                                    value={(currentViewData.monatsUmsaetze || [])[actualIndex]} 
                                                                    onValueChange={(val: number) => handleMonatsumsatzChange(actualIndex, val)} 
                                                                    placeholder="0" 
                                                                    suffix={currencySymbol} 
                                                                    tooltip={`Dein Brutto-Umsatz im ${monat}.`}
                                                                />
                                                            </div>
                                                        </div>
                                                    ); 
                                                })}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {showDurchschnittsFeld && (
                                <div className={`max-w-md ${showMonatsTabelle ? "pt-8 border-t border-pe-border dark:border-slate-800" : ""}`}>
                                    <SmartIntegerInput 
                                        label="Ø Brutto-Monatsumsatz (Pauschal)" 
                                        value={currentViewData.durchschnittsumsatz} 
                                        onValueChange={(val: number) => handleDataChange('durchschnittsumsatz', val)} 
                                        suffix={currencySymbol} 
                                        tooltip="Falls du keine Einzelmonate eingeben willst: Dein durchschnittlicher Brutto-Monatsumsatz."
                                    />
                                </div>
                            )}
                        </div>

                        {/* 💉 NOBLE SLATE: AGGREGIERTE MASTER-ANSICHT */}
                        <div className="pt-12">
                            <div className="flex items-center justify-center gap-4 mb-10">
                                <div className="h-px bg-pe-border dark:bg-slate-800 flex-1 max-w-xs"></div>
                                <h3 className="text-2xl font-black text-pe-text dark:text-white uppercase tracking-widest text-center">
                                    Kalkulationsergebnisse {isMultiFounder ? <span className="text-pe-accent-text dark:text-[#E6C299]">(Summiert)</span> : ''}
                                </h3>
                                <div className="h-px bg-pe-border dark:bg-slate-800 flex-1 max-w-xs"></div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {/* UMSATZKALKULATION (SOLL) */}
                                <div className="bg-pe-card dark:bg-[#15171e] p-6 rounded-2xl border border-pe-border dark:border-slate-800 shadow-xl relative overflow-hidden group hover:border-[#E6C299]/30 transition-colors">
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-[#E6C299]/5 rounded-full blur-3xl -mr-10 -mt-10 transition-all group-hover:bg-[#E6C299]/10"></div>
                                    <div className="text-pe-accent-text dark:text-[#E6C299] font-black text-xs uppercase tracking-widest mb-6 pb-4 border-b border-pe-border dark:border-slate-800">
                                        Umsatzkalkulation (Soll)
                                    </div>
                                    <div className="space-y-4">
                                        <FormulaResult label="Bruttoumsatz p. Std." value={formatCurrency(aggBruttoumsatzProStunde) + " " + currencySymbol} formula={`Ø Umsatz / (WoStd. * ${wochensatz})`} show={showFormulas} tooltip="Das musst du pro Stunde umsetzen, um deine Kosten zu decken." />
                                        <FormulaResult label="Ø Monatsumsatz (Brutto)" value={formatCurrency(aggErwarteterBrutto) + " " + currencySymbol} formula="Eingabe Ø" show={showFormulas} tooltip="Dein kalkulierter Durchschnittsumsatz (Brutto)." />
                                        <FormulaResult label="Ø Monatsumsatz (Netto)" value={formatCurrency(aggErwarteterNetto) + " " + currencySymbol} formula={`Brutto / 1.19`} show={showFormulas} tooltip="Dein Umsatz abzüglich Mehrwertsteuer." />
                                    </div>
                                </div>

                                {/* UMSATZ REAL (IST) */}
                                <div className="bg-pe-card dark:bg-[#15171e] p-6 rounded-2xl border border-pe-border dark:border-slate-800 shadow-xl relative overflow-hidden group hover:border-[#E6C299]/30 transition-colors">
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-[#E6C299]/5 rounded-full blur-3xl -mr-10 -mt-10 transition-all group-hover:bg-[#E6C299]/10"></div>
                                    <div className="text-pe-accent-text dark:text-[#E6C299] font-black text-xs uppercase tracking-widest mb-6 pb-4 border-b border-pe-border dark:border-slate-800">
                                        Real-Umsatz (Ist)
                                    </div>
                                    
                                    {isMultiFounder ? (
                                        <div className="space-y-4 max-h-[350px] overflow-y-auto custom-scrollbar pr-2">
                                            {pgData.partner.map((p: any) => (
                                                <div key={p.id} className="bg-pe-tile-header dark:bg-[#08090b] border border-pe-border dark:border-slate-800 rounded-xl p-4 mb-4 last:mb-0">
                                                    <div className="text-[10px] text-orange-700 dark:text-orange-400 font-bold uppercase tracking-widest mb-3 truncate">{p.name || 'Unbenannt'}</div>
                                                    <div className="space-y-3">
                                                        <InputWithTooltip label={`Ø Real-Umsatz HJ1`} value={formatCurrency(0)} readOnly suffix={currencySymbol} tooltip="Wird in Zukunft über die persönliche Leistungsakte synchronisiert." />
                                                        <InputWithTooltip label={`Ø Real-Umsatz HJ2`} value={formatCurrency(0)} readOnly suffix={currencySymbol} tooltip="Wird in Zukunft über die persönliche Leistungsakte synchronisiert." />
                                                    </div>
                                                </div>
                                            ))}
                                            <div className="pt-2 mt-2">
                                                <InputWithTooltip label="Gesamt Real-Umsatz HJ1" value={formatCurrency(realUnternehmerSchnittHJ1)} readOnly suffix={currencySymbol} tooltip="Gesamtsumme (aktuell noch global ermittelt)." />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            <InputWithTooltip 
                                                label="Ø Real-Umsatz HJ1 (Ist)" 
                                                value={formatCurrency(realUnternehmerSchnittHJ1)} 
                                                readOnly={true} 
                                                suffix={currencySymbol} 
                                                tooltip="Automatisch berechnet aus deinen eingegebenen Monatsumsätzen (Jan-Jun)."
                                            />
                                            <InputWithTooltip 
                                                label="Ø Real-Umsatz HJ2 (Ist)" 
                                                value={formatCurrency(realUnternehmerSchnittHJ2)} 
                                                readOnly={true} 
                                                suffix={currencySymbol} 
                                                tooltip="Automatisch berechnet aus deinen eingegebenen Monatsumsätzen (Jul-Dez)."
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* STEUERN */}
                                <div className="bg-pe-card dark:bg-[#15171e] p-6 rounded-2xl border border-pe-border dark:border-slate-800 shadow-xl relative overflow-hidden group hover:border-[#E6C299]/30 transition-colors">
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-[#E6C299]/5 rounded-full blur-3xl -mr-10 -mt-10 transition-all group-hover:bg-[#E6C299]/10"></div>
                                    <div className="text-pe-accent-text dark:text-[#E6C299] font-black text-xs uppercase tracking-widest mb-6 pb-4 border-b border-pe-border dark:border-slate-800">
                                        Steuern
                                    </div>
                                    <div className="space-y-4">
                                        <FormulaResult label="Zu erwartende Einkommenssteuer (Monat)" value={formatCurrency(aggErwarteteSteuer) + " " + currencySymbol} formula={`Gesamtlohn * Steuersatz%`} show={showFormulas} tooltip="Rücklage für die private Einkommenssteuer." />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UnternehmerwertePage;